import React from "react";
import { useNavigate } from "react-router-dom";
import { Clock, User, ArrowRight } from "lucide-react";
import { SEVERITY_META, categoryIcon } from "@/lib/meta";
import { Badge } from "@/components/ui/badge";
import { RUNBOOK } from "@/constants/testIds";
import { cn } from "@/lib/utils";

export default function RunbookCard({ runbook }) {
  const navigate = useNavigate();
  const sev = SEVERITY_META[runbook.severity] || SEVERITY_META.P3;
  const Icon = categoryIcon(runbook.category);

  return (
    <button
      type="button"
      data-testid={RUNBOOK.card(runbook.id)}
      onClick={() => navigate(`/runbooks/${runbook.id}`)}
      className="group text-left rounded-xl border border-border bg-card p-5 flex flex-col gap-3 hover:border-primary/50 hover:shadow-lg hover:shadow-primary/5 transition-colors tr-fade-up"
    >
      <div className="flex items-center justify-between">
        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
          <Icon className="h-4 w-4" /> {runbook.category}
        </span>
        <Badge variant="outline" className={cn("border text-xs", sev.badge)}>
          {sev.label}
        </Badge>
      </div>

      <h3
        data-testid={RUNBOOK.cardTitle(runbook.id)}
        className="font-display font-semibold text-base sm:text-lg leading-snug group-hover:text-primary transition-colors"
      >
        {runbook.title}
      </h3>

      {runbook.summary && (
        <p className="text-sm text-muted-foreground line-clamp-2 leading-relaxed">{runbook.summary}</p>
      )}

      <div className="flex flex-wrap gap-1.5 mt-auto pt-1">
        {(runbook.tags || []).slice(0, 4).map((t) => (
          <span key={t} className="font-mono text-[11px] rounded border border-border bg-secondary/60 px-1.5 py-0.5 text-muted-foreground">
            {t}
          </span>
        ))}
      </div>

      <div className="flex items-center justify-between border-t border-border pt-3 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1">
          <Clock className="h-3.5 w-3.5" /> {runbook.estimated_time || "—"}
        </span>
        <span className="inline-flex items-center gap-1 truncate">
          <User className="h-3.5 w-3.5" /> {runbook.owner}
        </span>
        <ArrowRight className="h-4 w-4 opacity-0 group-hover:opacity-100 -translate-x-1 group-hover:translate-x-0 transition-all text-primary" />
      </div>
    </button>
  );
}
